import { api } from './api';
import { currentLang } from './i18n';
import { cachedAllGoals, hasLoadedGoals, isGoalsLoading, sessionGeneration } from './state';
import { applyWorkChanges, checkedIds, chosenOptions, leavingKeys, settleWorkChanges, type PendingWorkChange, type WorkListChange } from './work-list-pending';
import { scopeWorkList, type WorkFilter, type WorkItem, type WorkList, type WorkRange } from './work-list-view';
import type { WorkDetailTarget } from './work-detail-view';
import type { WorkAchievement } from './components/work-achievement';
import type { GoalWithSubtasks } from './types';
import { goalHasCustomer, loadGoalCustomers, onGoalCustomersRead, resetGoalCustomers } from './goal-groupings';

// #2749: the Web's first tab shows the list the server derives
// (GET /api/work-list), the same rows the extension and Claude read.

type WorkListElement = HTMLElement & {
  list?: WorkList | null;
  filter?: WorkFilter;
  lowExpanded?: boolean;
  selected?: Set<string>;
  selecting?: boolean;
  busy?: Set<string>;
  leaving?: Set<string>;
  chosen?: Map<string, string>;
  openKey?: string | null;
  customerFilter?: string | null;
  customersRead?: number;
};

type WorkDetailElement = HTMLElement & {
  target?: WorkDetailTarget | null;
  goal?: GoalWithSubtasks | null;
  item?: WorkItem | null;
  today?: string;
  loading?: boolean;
  updateComplete?: Promise<unknown>;
  focusHeading?: () => void;
};

export const workListState = {
  /** What the list shows. */
  list: null as WorkList | null,
  failed: false,
  /** 今日 or すべて in the menu (#2856). */
  range: 'today' as WorkRange,
  filter: null as WorkFilter,
  lowExpanded: false,
  /** Item keys of goal candidates and completion proposals chosen for the bulk bar. */
  selected: new Set<string>(),
  /** Yoshie 2026-10-29: goal cards show a selection check, to complete or drop them together. */
  selecting: false,
  /** The list as last read from the server; `list` is it with `pending` laid over it. */
  snapshot: null as WorkList | null,
  /** #3549: changes made here that the last read does not have yet. */
  pending: new Map<number, PendingWorkChange>(),
  /** What the detail beside the list shows; null when it is closed. */
  detail: null as WorkDetailTarget | null,
  /** The card shown above the list after a goal is completed. */
  achievement: null as WorkAchievement | null,
  /** #3805: the customer domain the list is narrowed to; null shows every goal. */
  customerFilter: null as string | null,
  /** #3805: counts the customer reads, so the list draws their marks again. */
  customersRead: 0,
};

onGoalCustomersRead(() => {
  workListState.customersRead += 1;
  renderWorkList();
});

let readsStarted = 0;
const readNumbers = new WeakMap<Promise<WorkList>, number>();

export function requestWorkList(): Promise<WorkList> {
  const request = api('/api/work-list') as Promise<WorkList>;
  readNumbers.set(request, ++readsStarted);
  return request;
}

function showChanges() {
  const { snapshot, pending } = workListState;
  workListState.list = snapshot ? applyWorkChanges(snapshot, pending.values()) : null;
}

let changesStarted = 0;

/**
 * #3549: shows a change on the list at once, before it is saved. Leaving rows
 * play their exit for `exitMs` first. Pass the returned id to
 * finishWorkListChange when the save is through or has failed.
 */
export function startWorkListChange(changes: WorkListChange[], exitMs = 0): number {
  const id = ++changesStarted;
  const exiting = exitMs > 0 && changes.some((change) => change.type === 'leave');
  workListState.pending.set(id, { changes, exiting, savedAt: null });
  if (exiting) {
    window.setTimeout(() => {
      const entry = workListState.pending.get(id);
      if (!entry) return;
      entry.exiting = false;
      showChanges();
      renderWorkList();
    }, exitMs);
  }
  showChanges();
  renderWorkList();
  return id;
}

/**
 * A saved change stays until a read started after now has it; a change that
 * was not saved is taken back at once, and the rows and counts with it.
 */
export function finishWorkListChange(id: number, saved: boolean) {
  const entry = workListState.pending.get(id);
  if (!entry) return;
  if (saved) {
    entry.savedAt = readsStarted;
    return;
  }
  workListState.pending.delete(id);
  showChanges();
  renderWorkList();
}

/** Shows a change, runs its save, and keeps or takes back the change by the outcome. */
export async function withWorkListChange(changes: WorkListChange[], save: () => Promise<boolean>, exitMs = 0): Promise<boolean> {
  const id = startWorkListChange(changes, exitMs);
  let saved = false;
  try {
    saved = await save();
  } finally {
    finishWorkListChange(id, saved);
  }
  return saved;
}

/** Reads the list (or takes a request already started) and draws it. */
/** #3138: on sign-out nothing of the previous account's list is kept to draw again. */
export function resetWorkListState() {
  workListState.list = null;
  workListState.failed = false;
  workListState.filter = null;
  workListState.lowExpanded = false;
  workListState.selecting = false;
  workListState.selected.clear();
  workListState.snapshot = null;
  workListState.pending.clear();
  workListState.detail = null;
  workListState.achievement = null;
  workListState.customerFilter = null;
  resetGoalCustomers();
}

export async function loadWorkList(request?: Promise<WorkList>) {
  const generation = sessionGeneration;
  const pending = request ?? requestWorkList();
  const number = readNumbers.get(pending) ?? 0;
  try {
    const list = await pending;
    if (generation !== sessionGeneration) return;
    settleWorkChanges(workListState.pending, number);
    workListState.snapshot = list;
    showChanges();
    workListState.failed = false;
    const keys = new Set(list.items.map((item) => item.key));
    for (const key of [...workListState.selected]) {
      if (!keys.has(key)) workListState.selected.delete(key);
    }
  } catch {
    if (generation !== sessionGeneration) return;
    workListState.failed = true;
  }
  renderWorkList();
  if (!workListState.failed) void loadGoalCustomers();
}

let reloading: Promise<void> | null = null;
let reloadAgain = false;

/**
 * Reads the list again after a change. Changes made while a read runs share
 * one more read after it, so checking several goals in a row does not start
 * a slow read for each.
 */
export function reloadWorkList(): Promise<void> {
  if (reloading) {
    reloadAgain = true;
    return reloading;
  }
  reloading = (async () => {
    do {
      reloadAgain = false;
      await loadWorkList();
    } while (reloadAgain);
  })().finally(() => { reloading = null; });
  return reloading;
}

function show(id: string, visible: boolean) {
  const el = document.getElementById(id);
  if (el) el.style.display = visible ? '' : 'none';
}

function renderError(container: HTMLElement) {
  const message = document.createElement('div');
  message.className = 'msg-error';
  message.textContent = currentLang === 'en'
    ? 'Could not load your list. Please try again later.'
    : '一覧を読み込めませんでした。しばらくしてから再度お試しください。';
  container.replaceChildren(message);
}

function detailKey(target: WorkDetailTarget | null): string | null {
  return target?.key ?? null;
}

/**
 * Draws the detail of the open row from the goals already read. A task or goal
 * that is gone closes it; a task done since it was opened stays while its goal
 * is in the list, so it can be undone.
 */
export function renderWorkDetail() {
  const pane = document.getElementById('workDetail');
  const layout = document.getElementById('workLayout');
  const el = pane?.querySelector('zen-work-detail') as WorkDetailElement | null;
  if (!pane || !el) return;
  let target = workListState.detail;
  const list = workListState.list;
  // #2857: a detail opened from a row follows that row. When the row is gone
  // the goal's other row takes over (a card that became 「次にやること」);
  // with no row left for the goal, the detail closes instead of staying
  // beside a list that no longer shows it.
  if (target?.key && list && !list.items.some((row) => row.key === target!.key)) {
    const goalId = target.type === 'candidate' ? null : target.goalId;
    const sibling = goalId ? list.items.find((row) => row.goal_id === goalId && !row.candidate_id) : null;
    target = sibling && target.type !== 'candidate' ? { ...target, key: sibling.key } : null;
    workListState.detail = target;
  }
  const item = target?.key ? list?.items.find((row) => row.key === target!.key) ?? null : null;
  const goal = target && target.type !== 'candidate' ? cachedAllGoals.find((g) => g.id === target.goalId) ?? null : null;
  // A row opened while the goals are still being read shows that it is
  // reading, instead of doing nothing until its goal arrives.
  const waiting = !!target && target.type !== 'candidate' && !goal && !!item && isGoalsLoading;
  const gone = !target
    || (target.type === 'candidate' ? !item : !goal && !waiting);
  if (gone) {
    workListState.detail = null;
    pane.hidden = true;
    layout?.classList.remove('has-detail');
    el.target = null;
    return;
  }
  pane.hidden = false;
  layout?.classList.add('has-detail');
  el.target = target;
  el.goal = goal;
  el.loading = waiting;
  el.item = item;
  el.today = list?.today ?? '';
}

function renderAchievement() {
  const el = document.getElementById('workAchievement') as (HTMLElement & { achievement?: WorkAchievement | null }) | null;
  if (el) el.achievement = workListState.achievement;
}

export function renderWorkList() {
  renderAchievement();
  renderWorkDetail();
  const container = document.getElementById('todayList');
  const empty = document.getElementById('todayEmpty');
  const full = workListState.list;
  const scoped = full ? scopeWorkList(full, workListState.range) : null;
  // #3805: a customer chosen narrows the rows to that customer's goals.
  const customer = workListState.customerFilter;
  const list = scoped && customer
    ? { ...scoped, items: scoped.items.filter((item) => !!item.goal_id && goalHasCustomer(item.goal_id, customer)) }
    : scoped;
  if (!container || !empty) return;

  // Rows are drawn before the goals arrive; an empty list waits for them to
  // tell a new account from a finished day.
  const rowsReady = !!list && list.items.length > 0;
  if ((!hasLoadedGoals && !rowsReady) || (!list && !workListState.failed)) {
    empty.style.display = 'none';
    return;
  }
  document.getElementById('todayLoading')?.classList.remove('show');
  if (!list) {
    empty.style.display = 'none';
    renderError(container);
    return;
  }

  if (list.items.length === 0 && list.sources.goals === 'ok' && list.sources.candidates === 'ok' && !customer) {
    container.replaceChildren();
    empty.style.display = 'block';
    // 今日 has nothing due while すべて still has rows dated later.
    const laterOnly = (full?.items.length ?? 0) > 0;
    const noGoals = !laterOnly && cachedAllGoals.length === 0;
    const allDone = !laterOnly && !noGoals && cachedAllGoals.every((goal) => goal.status === 'done');
    show('todayOnboarding', noGoals);
    show('todayCongrats', allDone);
    show('todayLaterOnly', laterOnly);
    show('todayNoGoals', !laterOnly && !noGoals && !allDone);
    return;
  }

  empty.style.display = 'none';
  let el = container.querySelector('zen-work-list') as WorkListElement | null;
  if (!el) {
    el = document.createElement('zen-work-list') as WorkListElement;
    container.replaceChildren(el);
  }
  el.list = list;
  el.filter = workListState.filter;
  el.lowExpanded = workListState.lowExpanded;
  // New Sets so the element sees the change and draws again.
  el.selected = new Set(workListState.selected);
  el.selecting = workListState.selecting;
  el.busy = checkedIds(workListState.pending.values());
  el.leaving = leavingKeys(list, workListState.pending.values());
  el.chosen = chosenOptions(workListState.pending.values());
  el.openKey = detailKey(workListState.detail);
  el.customerFilter = customer;
  el.customersRead = workListState.customersRead;
}

/** Opens a detail and moves focus to it, so the keyboard follows the reader. */
export async function openWorkDetail(target: WorkDetailTarget) {
  workListState.detail = target;
  renderWorkList();
  const el = document.querySelector('#workDetail zen-work-detail') as WorkDetailElement | null;
  await el?.updateComplete;
  el?.focusHeading?.();
  if (window.matchMedia?.('(max-width: 1099px)').matches) document.getElementById('workDetail')?.scrollTo?.(0, 0);
}

/**
 * Scrolls the list to the open row, for a detail opened from outside the list
 * (a search result), so the row sits beside its detail. On a narrow screen the
 * detail is a sheet over the list, so the list is left where it is.
 */
export async function revealOpenWorkRow() {
  if (window.matchMedia?.('(max-width: 1099px)').matches) return;
  const el = document.querySelector('#todayList zen-work-list') as (HTMLElement & { updateComplete?: Promise<unknown> }) | null;
  await el?.updateComplete;
  el?.querySelector('.work-row.open')?.scrollIntoView({ block: 'center' });
}

/** Closes the detail and gives focus back to the row it was opened from. */
export function closeWorkDetail() {
  const key = detailKey(workListState.detail);
  workListState.detail = null;
  renderWorkList();
  if (key) (document.querySelector(`#todayList [data-work-key="${CSS.escape(key)}"]`) as HTMLElement | null)?.focus();
}
